import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Sparkles, ArrowRight, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTO_OPEN_KEY = "maxsc_chat_auto_opened";

const SUGGESTED = [
    "How do I get started?",
    "What's the difference between your programs?",
    "How much does training cost?",
    "What ages do you train?",
    "What happens at the Initial session?",
    "Where are you located and what are your hours?",
];

/**
 * Floating chat bubble for the public-facing site.
 * - Bottom-right; pulses a subtle nudge after first idle moment to draw attention
 * - Click to open a 380px panel with chips, conversation, send field
 * - "Book your Initial session" markdown link in bot replies opens the assessment modal
 *   via the global custom event 'maxsc:open-assessment' that Home.jsx already listens to,
 *   OR scrolls to the home form if user is on home page.
 */
export default function ChatBubble({ onAssessmentClick }) {
    const [open, setOpen] = useState(false);
    const [pulse, setPulse] = useState(false);
    const [messages, setMessages] = useState([]); // {role, content}
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const scrollRef = useRef(null);

    // Auto-open the chat ~1s after landing — once per session so we don't nag returning users.
    // Users on mobile keyboards or who closed it before won't be re-prompted in the same tab.
    // Skip auto-open on mobile (< md) — the panel covers the whole viewport
    // there and hides the actual page content. Visitors can still tap the
    // bubble. Desktop / tablet keep the proactive prompt.
    useEffect(() => {
        try {
            if (sessionStorage.getItem(AUTO_OPEN_KEY)) return;
        } catch { /* sessionStorage may be unavailable */ }
        const isMobile = typeof window !== "undefined" && window.matchMedia
            ? window.matchMedia("(max-width: 767px)").matches
            : false;
        if (isMobile) return;
        const t = setTimeout(() => {
            setOpen(true);
            try { sessionStorage.setItem(AUTO_OPEN_KEY, "1"); } catch { /* noop */ }
        }, 1500);
        return () => clearTimeout(t);
    }, []);

    // Auto-pulse after 4s of being idle, once per pageload, only if not opened yet
    useEffect(() => {
        const t = setTimeout(() => setPulse(true), 4000);
        return () => clearTimeout(t);
    }, []);
    useEffect(() => { if (open) setPulse(false); }, [open]);

    // Scroll-to-top whenever a new question is asked (menu → answer transition).
    // Keeps the user at the start of the answer so long replies are readable from
    // beginning to end, instead of auto-bottoming like a chat thread.
    useEffect(() => {
        if (scrollRef.current && messages.length > 0) {
            scrollRef.current.scrollTop = 0;
        }
    }, [messages.length]);

    // Back to the question menu — clears the local thread and starts a fresh conversation
    // server-side on the next click.
    const goBackToMenu = () => {
        setMessages([]);
        setConversationId(null);
        setInput("");
    };

    const send = async (text) => {
        const msg = (text || input).trim();
        if (!msg || sending) return;
        setInput("");
        setMessages((prev) => [...prev, { role: "user", content: msg }]);
        setSending(true);
        try {
            const r = await axios.post(`${API}/chat/message`, {
                conversation_id: conversationId,
                message: msg,
            });
            if (!conversationId && r.data.conversation_id) {
                setConversationId(r.data.conversation_id);
            }
            setMessages((prev) => [...prev, { role: "assistant", content: r.data.reply }]);
        } catch (e) {
            const detail = e?.response?.data?.detail || "Something went wrong. Try again in a sec.";
            setMessages((prev) => [...prev, { role: "assistant", content: `_${detail}_` }]);
        } finally {
            setSending(false);
        }
    };

    const handleAssessment = () => {
        // Fire-and-forget telemetry: this is the chatbot's conversion event
        if (conversationId) {
            axios.post(`${API}/chat/cta-click`, {
                conversation_id: conversationId,
                event: "assessment_cta_click",
            }).catch(() => {});
        }
        setOpen(false);
        if (typeof onAssessmentClick === "function") {
            onAssessmentClick();
        } else {
            // Fallback: scroll to assessment form on the home page
            const el = document.querySelector("#book-assessment-form, [data-assessment-form]");
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            } else {
                window.location.href = "/#book-assessment-form";
            }
        }
    };

    return (
        <>
            {/* Floating bubble */}
            {!open && (
                <button
                    type="button"
                    data-testid="chat-bubble-toggle"
                    onClick={() => setOpen(true)}
                    aria-label="Open chat with Max"
                    className={`fixed bottom-24 sm:bottom-20 right-5 z-50 flex items-center gap-2 bg-brand-navy-800 hover:bg-brand-navy-700 !text-white px-4 py-3 rounded-full shadow-2xl shadow-brand-navy-900/50 ring-1 ring-white/10 ${
                        pulse ? "animate-bubble-pulse" : ""
                    } transition-transform hover:scale-105`}
                >
                    <span className="absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full bg-brand-red ring-2 ring-brand-obsidian" />
                    <MessageCircle className="w-5 h-5 text-white" strokeWidth={2.25} />
                    <span className="text-sm font-semibold tracking-wide text-white">Ask Max</span>
                </button>
            )}

            {/* Chat panel */}
            {open && (
                <div
                    data-testid="chat-panel"
                    className="fixed bottom-24 sm:bottom-20 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-96 h-[600px] max-h-[80vh] bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-chat-in"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-brand-navy-900 bg-gradient-to-r from-brand-navy-900/60 via-zinc-950 to-zinc-950">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-9 h-9 rounded-full bg-brand-navy-700 text-white flex items-center justify-center font-display tracking-wider">M</div>
                            <div className="min-w-0">
                                <div className="font-display tracking-wider text-white truncate">MAX</div>
                                <div className="text-[10px] tracking-wider uppercase text-zinc-300 inline-flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-brand-navy-300 animate-pulse" />
                                    MaxSC concierge
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="chat-close"
                            onClick={() => setOpen(false)}
                            aria-label="Close chat"
                            className="p-1.5 text-zinc-400 hover:text-white"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Body — switches between MENU and ANSWER views */}
                    {messages.length === 0 ? (
                        // ============== MENU VIEW ==============
                        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 text-sm">
                            <div className="bg-zinc-900 text-zinc-100 rounded-2xl rounded-bl-sm px-3.5 py-2.5 max-w-[85%]">
                                <p>
                                    <span className="font-semibold text-white">Hi — I'm Max.</span> I'm the
                                    concierge at Maximum Sports Conditioning.
                                </p>
                            </div>
                            <div className="bg-zinc-900 text-zinc-100 rounded-2xl rounded-bl-sm px-3.5 py-2.5 max-w-[85%]">
                                <p>Tap a question below to see the answer — or type your own.</p>
                            </div>

                            <div className="pt-3">
                                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-navy-300 mb-2 inline-flex items-center gap-2">
                                    <span className="inline-block w-6 h-px bg-brand-navy-600" />
                                    Common Questions
                                </div>
                                <div className="space-y-2">
                                    {SUGGESTED.map((q) => (
                                        <button
                                            key={q}
                                            type="button"
                                            data-testid={`chat-chip-${q.slice(0, 12).toLowerCase().replace(/\s+/g, "-")}`}
                                            onClick={() => send(q)}
                                            className="group w-full flex items-center justify-between gap-3 text-left text-sm text-zinc-100 border border-zinc-800 bg-zinc-900/40 hover:border-brand-navy-500 hover:bg-brand-navy-900/40 rounded-lg px-3.5 py-2.5 transition-colors"
                                        >
                                            <span className="inline-flex items-center gap-2 leading-snug">
                                                <Sparkles className="w-3.5 h-3.5 text-brand-navy-300 flex-shrink-0" />
                                                {q}
                                            </span>
                                            <ArrowRight className="w-3.5 h-3.5 text-zinc-500 group-hover:text-white transition-colors flex-shrink-0" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        // ============== ANSWER VIEW ==============
                        <>
                            <button
                                type="button"
                                data-testid="chat-back-to-menu"
                                onClick={goBackToMenu}
                                className="flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase text-brand-navy-300 hover:text-white hover:bg-brand-navy-900/40 border-b border-zinc-800 transition-colors"
                            >
                                <ArrowLeft className="w-3.5 h-3.5" />
                                Back to questions
                            </button>
                            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 text-sm">
                                {messages.map((m, i) => (
                                    <Bubble key={i} message={m} onAssessmentClick={handleAssessment} />
                                ))}
                                {sending && (
                                    <div className="bg-zinc-900 text-zinc-300 rounded-2xl rounded-bl-sm px-3.5 py-2.5 max-w-[60%] inline-flex items-center gap-2 text-xs">
                                        <span className="flex gap-1">
                                            <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-typing-dot" style={{ animationDelay: "0ms" }} />
                                            <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-typing-dot" style={{ animationDelay: "150ms" }} />
                                            <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-typing-dot" style={{ animationDelay: "300ms" }} />
                                        </span>
                                        Max is typing…
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Input */}
                    <form
                        onSubmit={(e) => { e.preventDefault(); send(); }}
                        className="border-t border-zinc-800 p-3 flex items-center gap-2"
                    >
                        <input
                            data-testid="chat-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Type your message…"
                            disabled={sending}
                            maxLength={1500}
                            className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-brand-navy-500 focus:ring-1 focus:ring-brand-navy-500/40 disabled:opacity-60"
                        />
                        <button
                            type="submit"
                            data-testid="chat-send"
                            disabled={sending || !input.trim()}
                            aria-label="Send message"
                            className="p-2.5 rounded-lg bg-brand-navy-700 text-white hover:bg-brand-navy-600 disabled:opacity-50"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </button>
                    </form>
                </div>
            )}

            <style>{`
                @keyframes bubble-pulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(31,45,85,.6); transform: scale(1); }
                    50%      { box-shadow: 0 0 0 14px rgba(31,45,85,0); transform: scale(1.04); }
                }
                .animate-bubble-pulse { animation: bubble-pulse 1.6s ease-out 3; }
                @keyframes chat-in {
                    from { opacity: 0; transform: translateY(12px) scale(.98); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .animate-chat-in { animation: chat-in .22s ease-out; }
                @keyframes typing-dot {
                    0%, 60%, 100% { opacity: .3; transform: translateY(0); }
                    30%           { opacity: 1; transform: translateY(-2px); }
                }
                .animate-typing-dot { animation: typing-dot 1.2s infinite; }
                @keyframes chip-tray {
                    from { opacity: 0; transform: translateY(6px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .animate-chip-tray { animation: chip-tray .18s ease-out; }
            `}</style>
        </>
    );
}

/**
 * One message bubble. Renders the bot's "[Book your Initial session](#book-assessment)"
 * markdown link as a real CTA button inline.
 */
function Bubble({ message, onAssessmentClick }) {
    const isUser = message.role === "user";
    const content = message.content || "";

    // Detect the special "[Book your Initial session](#book-assessment)" marker,
    // optionally wrapped in **bold**. We strip the marker + its bold wrappers so
    // we can render the rest of the reply through the real Markdown component
    // (otherwise `**emphasis**` shows up as literal asterisks).
    const ASSESS_RE = /\*{0,2}\[([^\]]+)\]\(#book-assessment\)\*{0,2}/i;
    const match = !isUser && ASSESS_RE.exec(content);
    const body = match ? content.replace(ASSESS_RE, "").trim() : content;

    return (
        <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
            <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 whitespace-pre-wrap leading-relaxed ${
                    isUser
                        ? "bg-brand-navy-700 text-white rounded-br-sm"
                        : "bg-zinc-900 text-zinc-100 rounded-bl-sm"
                }`}
            >
                {body && <Markdown text={body} />}
                {match && (
                    <button
                        type="button"
                        data-testid="chat-cta-assessment"
                        onClick={onAssessmentClick}
                        className="mt-3 inline-flex items-center gap-1.5 bg-brand-red text-white px-3 py-2 rounded-lg text-xs tracking-wider uppercase hover:bg-brand-red/90"
                    >
                        {match[1]} <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}

/** Light-touch markdown: **bold**, line breaks, bullet lists. No external lib. */
function Markdown({ text }) {
    const parts = text.split(/\n\n+/);
    return (
        <>
            {parts.map((p, i) => {
                const lines = p.split("\n");
                const isList = lines.every((l) => /^[-*]\s/.test(l.trim()));
                if (isList) {
                    return (
                        <ul key={i} className={`list-disc pl-5 ${i > 0 ? "mt-2" : ""}`}>
                            {lines.map((l, j) => (
                                <li key={j}>{renderBold(l.replace(/^[-*]\s+/, ""))}</li>
                            ))}
                        </ul>
                    );
                }
                return <p key={i} className={i > 0 ? "mt-2" : ""}>{renderBold(p)}</p>;
            })}
        </>
    );
}

function renderBold(text) {
    // Combined inline markdown: [label](url) links + **bold** (links can be inside bold).
    // Walk token by token so the outer bold doesn't swallow an inner link as raw text.
    const out = [];
    let key = 0;
    let i = 0;
    while (i < text.length) {
        // 1) **bold** — recurse on the inner content so embedded links still render.
        if (text.startsWith("**", i)) {
            const end = text.indexOf("**", i + 2);
            if (end !== -1) {
                const inner = text.slice(i + 2, end);
                out.push(
                    <strong key={`b-${key++}`} className="text-white font-semibold">
                        {renderBold(inner)}
                    </strong>
                );
                i = end + 2;
                continue;
            }
        }
        // 2) [label](http(s)://url)
        if (text[i] === "[") {
            const close = text.indexOf("]", i + 1);
            if (close !== -1 && text[close + 1] === "(") {
                const paren = text.indexOf(")", close + 2);
                const url = text.slice(close + 2, paren);
                if (paren !== -1 && /^https?:\/\//.test(url)) {
                    out.push(
                        <a
                            key={`l-${key++}`}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-navy-300 underline underline-offset-2 hover:text-white transition-colors"
                        >
                            {text.slice(i + 1, close)}
                        </a>
                    );
                    i = paren + 1;
                    continue;
                }
            }
        }
        // 3) plain char — accumulate runs to keep React keys stable
        let j = i;
        while (j < text.length && text[j] !== "*" && text[j] !== "[") j++;
        if (j > i) {
            out.push(text.slice(i, j));
            i = j;
        } else {
            out.push(text[i]);
            i++;
        }
    }
    return out;
}
